/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Button } from '@mui/material';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import EnvelopeWithBox from '../EnvelopeWithBox';
import EConsentModal from '../modals/EConsentModal';
import Styles from '../../css/GoGreenPage.module.css';
import ButtonBorder from '../ButtonBorder';
import { useCallback, useState } from 'react';
import useBorrowerData from '../../common/hooks/useBorrowerData';
import { useLoanContext } from '../../contexts/LoanContext';
import { isObjEmptyOrNull } from '../../common/UtilityFunctions';
import DefaultModal from '../modals/DefaultModal';
import LoadingModal from '../modals/LoadingModal';
import { useGoGreenContext, Action, Status, isBorrowerGoGreenEnrolled } from '../../contexts/GoGreenContext';
// Judith Espinal - SQC-766: shared enrollment helper with Documents Go Green CTA

const BorrowerGoGreen = () => {
  const {
    borrowerNumber,
    setBorrowerNumber,
    actionInProcess,
    setActionInProcess,
    status,
    setStatus,
    processGoGreenRequest,
    finishESignProcess,
    startESignProcess,
    currentBorrower,
    reloadBorrowerData,
    fetchingBorrowerData,
    modalButtons,
  } = useGoGreenContext();

  const getStatusModalContentText = useCallback(() => {
    let contentText = '';
    if (status === Status.READY || actionInProcess === Action.NOACTION) return contentText;
    if (status === Status.ERROR) {
      contentText =
        actionInProcess === Action.SIGN
          ? 'There was an error signing up for Go Green.'
          : 'There was an error opting out of Go Green.';
    } else if (status === Status.SUCCESS) {
      contentText =
        actionInProcess === Action.SIGN ? 'You are now enrolled in Go Green.' : 'You have opted out of Go Green.';
    } else if (status === Status.CONFIRMING) {
      contentText =
        'You are about to opt out of receiving electronic statements and disclosures. Please confirm to proceed.';
    }
    return contentText;
  }, [status, actionInProcess]);

  const modals = () => {
    return (
      <>
        {loadingModal()}
        {econsentModal()}
        {infoModal()}
      </>
    );
  };

  const loadingModal = () => {
    return (status === Status.LOADING || fetchingBorrowerData) && <LoadingModal open={true}></LoadingModal>;
  };

  const econsentModal = () => {
    return (
      status === Status.CONFIRMING &&
      actionInProcess === Action.SIGN && (
        <EConsentModal
          open={true}
          onCancel={() => {
            finishESignProcess();
          }}
          onAgree={() => {
            processGoGreenRequest();
          }}
        />
      )
    );
  };

  const infoModal = () => {
    const infoPresent =
      (status === Status.CONFIRMING && actionInProcess === Action.OPTOUT) ||
      status === Status.SUCCESS ||
      status === Status.ERROR;
    return (
      infoPresent && (
        <DefaultModal
          className={Styles.modalcontainer}
          open={true}
          title={<div className={Styles.modaltitle}>{status}</div>}
          content={<p className={Styles.modalcontent}> {getStatusModalContentText()}</p>}
          footer={<div className={Styles.buttonrow}>{modalButtons()}</div>}
        ></DefaultModal>
      )
    );
  };

  const mainText = () => {
    return (
      <p className={Styles.text}>
        {isBorrowerGoGreenEnrolled(currentBorrower)
          ? 'You are enrolled in Go Green. Click the button below to opt out.'
          : 'Click the button below to sign up for Go Green and receive electronic statements.'}
      </p>
    );
  };

  const goGreenEnvelope = () => {
    return (
      <EnvelopeWithBox blockName={'Go Green'}>
        <div className={Styles.form}>
          {mainText()}
          {modals()}
        </div>
      </EnvelopeWithBox>
    );
  };

  const goGreenButtons = () => {
    return (
      <ButtonBorder>
        <Button
          className={Styles.button}
          onClick={() => {
            startESignProcess(currentBorrower?.borrowerNumber);
          }}
        >
          {isBorrowerGoGreenEnrolled(currentBorrower) ? 'Opt Out' : 'Sign Up'}
        </Button>
      </ButtonBorder>
    );
  };

  return (
    <ButtonBorderWrapper>
      {goGreenEnvelope()}
      {goGreenButtons()}
    </ButtonBorderWrapper>
  );
};

export default BorrowerGoGreen;
